import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { settingsConfig } from '../../views/settings/SettingsConfig';
import SideBarNavItem from 'components/buttons/SideBarNavItem';
import DropdownButton from 'components/buttons/DropdownButton';
import { useSettingsDivision } from './SettingsDivisionContext';
import { useViewOnlyPackageRestriction, hasAnyAllowedRight } from 'services/permissions/permissionChecks';
import '../../style/scss/navigation.scss';

// Renders the division selector and the settings menu groups
const SettingsMenuContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const { packageFeatures, activeFeatures, allowedRights, environmentType } = useAuthContext();
    const { divisions, selectedDivision, setSelectedDivisionId, isEnvironmentSelected, showDivisionSelector, refetchDivisions } = useSettingsDivision();

    // Determine whether the view only package hides some menu items
    const viewOnlyRestricted = useViewOnlyPackageRestriction();

    // Build the dropdown items with the whole environment option on top
    const divisionItems = [
        {
            label: 'nav.settings_environment_option',
            onClick: () => setSelectedDivisionId(null),
        },
        ...divisions.map(division => ({
            label: division.name,
            onClick: () => setSelectedDivisionId(division.id),
        })),
    ];

    return (
        <div className='container-medium settings-menu'>
            <div className='col-20'>
                {showDivisionSelector && (
                    // Refetch the divisions when opening the selector so new divisions show directly
                    <div className='settings-division-selector'
                         onMouseDown={() => refetchDivisions()}>
                        <DropdownButton
                            items={divisionItems}
                            label={selectedDivision?.name || 'nav.settings_environment_option'}
                            activeItem={selectedDivision?.name || 'nav.settings_environment_option'}
                            size='small'
                        />
                    </div>
                )}
                {Object.entries(settingsConfig).map(([groupName, groupConfig]) => {
                    // Hide the setting menus if hide for environment type is given
                    if (groupConfig.hideForEnvironmentType?.includes(environmentType)) {
                        return null;
                    }

                    // Filters out menu items which are not included in the package features
                    const visibleItems = groupConfig.items.filter(item => {
                        const hasPackageFeature = (!item.requiredFeature || packageFeatures.includes(item.requiredFeature));
                        const hasFeatureEnabled = (!item.requiredFeature || activeFeatures.includes(item.requiredFeature));

                        const isVisibleByFeature = item.showWhenDisabled ? hasPackageFeature : (hasPackageFeature && hasFeatureEnabled);

                        // Hide environment wide menu items when another division is selected
                        if (item.environmentWide && !isEnvironmentSelected) {
                            return false;
                        }

                        // Hide menu items which are not available for the view only package
                        if (item.hideForViewOnlyPackage && viewOnlyRestricted) {
                            return false;
                        }

                        // Hide menu items which require a right the user does not have
                        if (item.permissionCheck && !hasAnyAllowedRight(allowedRights, item.permissionCheck)) {
                            return false;
                        }

                        return (
                            isVisibleByFeature &&
                            (
                                !groupConfig.permissionCheck ||
                                hasAnyAllowedRight(allowedRights, groupConfig.permissionCheck)
                            )
                        );
                    });

                    return visibleItems.length > 0 && (
                        <div className='sidebar-nav-group' key={groupName}>
                            <h4>{t(groupConfig.label)}</h4>
                            <ul>
                                {visibleItems.map(item => (
                                    <SideBarNavItem key={item.route} to={item.route}>
                                        {t(item.label)}
                                    </SideBarNavItem>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
            <div className='col-80'>
                {children}
            </div>
        </div>
    );
};

const SettingsMenu: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <SettingsMenuContent>
            {children}
        </SettingsMenuContent>
    );
};

export default SettingsMenu;
